import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SSR_HEAD_SELECTOR = '[data-ssr="1"]';
const CLIENT_HEAD_SELECTOR = '[data-client-head="1"]';
const SITE_ORIGIN = "https://obsidianridge.ai";

const normalizedCanonicalForPath = (pathname: string): string => {
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  return `${SITE_ORIGIN}${normalizedPath}`;
};

const metadataIdentity = (node: Element): string | null => {
  const tagName = node.tagName.toLowerCase();
  if (tagName === "title") return "title";
  if (tagName === "meta") {
    for (const attribute of ["name", "property", "http-equiv", "itemprop"] as const) {
      const value = node.getAttribute(attribute);
      if (value) return `meta:${attribute}:${value.toLowerCase()}`;
    }
  }
  if (tagName === "link") {
    const rel = node.getAttribute("rel");
    if (rel) return `link:${rel.toLowerCase()}`;
  }
  return null;
};

interface SchemaIdentity {
  ids: Set<string>;
  types: Set<string>;
}

const schemaIdentity = (script: Element): SchemaIdentity | null => {
  try {
    const root = JSON.parse(script.textContent ?? "") as Record<string, unknown>;
    const nodes = [root];
    if (Array.isArray(root["@graph"])) {
      nodes.push(...(root["@graph"] as Record<string, unknown>[]));
    }

    const ids = new Set<string>();
    const types = new Set<string>();
    nodes.forEach((node) => {
      if (typeof node["@id"] === "string") ids.add(node["@id"]);
      const type = node["@type"];
      if (typeof type === "string") types.add(type);
      if (Array.isArray(type)) {
        type
          .filter((value): value is string => typeof value === "string")
          .forEach((value) => {
            types.add(value);
          });
      }
    });
    return { ids, types };
  } catch {
    return null;
  }
};

const schemasOverlap = (worker: SchemaIdentity, client: SchemaIdentity): boolean => {
  if ([...worker.ids].some((id) => client.ids.has(id))) return true;
  return [...worker.types].some((type) => client.types.has(type));
};

/**
 * The edge worker puts a complete crawl-ready head in the initial HTML. The
 * React 19 build then hoists the active route's Helmet tags into <head>.
 *
 * Keep the worker nodes in place while a lazy or data-backed route is still
 * loading. Once the current route's canonical (or noindex directive) proves
 * that the client head committed, remove only worker nodes with a client
 * equivalent. Unique worker JSON-LD remains available after hydration and is
 * removed only if it becomes stale after client-side navigation.
 */
export const HeadSsrAdoption = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const head = document.head;
    if (!head.querySelector(SSR_HEAD_SELECTOR)) return;
    const expectedCanonical = normalizedCanonicalForPath(pathname);

    let observer: MutationObserver | null = null;
    let stopped = false;

    const adoptWhenReady = (): boolean => {
      if (stopped || !head.querySelector(SSR_HEAD_SELECTOR)) return true;
      const clientCanonical = [
        ...head.querySelectorAll<HTMLLinkElement>(`link[rel="canonical"]${CLIENT_HEAD_SELECTOR}`),
      ].find((link) => link.href === expectedCanonical);
      const clientRobots = head.querySelector(`meta[name="robots"]${CLIENT_HEAD_SELECTOR}`);
      if (!clientCanonical && !clientRobots) return false;

      const workerCanonical = head.querySelector<HTMLLinkElement>(
        `link[rel="canonical"]${SSR_HEAD_SELECTOR}`
      )?.href;
      const clientNodes = [...document.querySelectorAll(CLIENT_HEAD_SELECTOR)];
      const clientMetadata = new Set(
        clientNodes
          .map(metadataIdentity)
          .filter((identity): identity is string => Boolean(identity))
      );
      const clientSchemas = clientNodes
        .filter((node) => node.matches('script[type="application/ld+json"]'))
        .map(schemaIdentity)
        .filter((identity): identity is SchemaIdentity => identity !== null);

      head.querySelectorAll(SSR_HEAD_SELECTOR).forEach((node) => {
        if (node.matches('script[type="application/ld+json"]')) {
          const route = node.getAttribute("data-ssr-route") ?? workerCanonical;
          const identity = schemaIdentity(node);
          const hasClientEquivalent =
            identity !== null && clientSchemas.some((client) => schemasOverlap(identity, client));
          const becameStale = Boolean(route && clientCanonical && route !== clientCanonical.href);

          if (hasClientEquivalent || becameStale) {
            node.remove();
          } else if (route) {
            node.setAttribute("data-ssr-route", route);
          }
          return;
        }

        const identity = metadataIdentity(node);
        if (identity && clientMetadata.has(identity)) node.remove();
      });
      return true;
    };

    if (!adoptWhenReady()) {
      observer = new MutationObserver(() => {
        if (adoptWhenReady()) observer?.disconnect();
      });
      observer.observe(head, { childList: true });
    }

    return () => {
      stopped = true;
      observer?.disconnect();
    };
  }, [pathname, search]);

  return null;
};

export default HeadSsrAdoption;
